/**
 * Auras (PLAN §3, TC SpellAuraEffects): apply / refresh (pandemic) / stacks, periodic
 * timers that tick while accumulated time >= period (optionally haste-scaled), expiry,
 * immunity, CC diminishing returns and procs.
 */
import type { AbilityId, AbilityOverride, AuraDef, AuraEvent, AuraInstance, AuraInstanceId, AuraId, CcType, HitType, Ms, ProcSpec, SimContext, Unit, UnitId } from '../contracts';
import { DR_MULTIPLIERS, DR_RESET_MS } from '../contracts';
import { evalCondition } from './conditions';
import { tickEchoes } from './clones'; // +rogue-specs
import { payShare } from './martyr'; // +acolyte-specs (S10)
import { tickPets } from './pets'; // +acolyte-specs (S7)
import { tickCompanions } from './pets'; // +ranger-specs (S7 companions)
import { retally } from './tally'; // +ranger-specs (Thicket)
import { runEffects } from './effects';
import { controlState, maxHpMult, rescaleMaxHp, unitEffectiveStats } from './stats';
import { addThreat } from './threat';
import { emitClean, isHostile, MAX_EFFECT_DEPTH, unitOf } from './util';

export type RemoveReason = 'expired' | 'dispelled' | 'cancelled' | 'death' | 'consumed' | 'replaced' | 'evade';

export interface ApplyAuraOpts {
  stacks?: number;
  absorb?: number;
  durationMs?: Ms;
  depth?: number;
}

/** CC categories that cancel the holder's cast. */
const CAST_BREAKING_CC: ReadonlySet<CcType> = new Set<CcType>(['stun', 'incapacitate', 'disorient', 'silence']);
const MOVE_BREAKING_CC: ReadonlySet<CcType> = new Set<CcType>(['stun', 'incapacitate', 'disorient', 'root']);

const PANDEMIC_CAP = 1.3;

/** Hit types that count for an 'avoided' proc when `ProcSpec.hitTypes` is not set. */
const DEFAULT_AVOID_TYPES: readonly HitType[] = ['dodge', 'parry', 'block'];

/**
 * +lead: rotation rework (F2). Rebuild `unit.abilityOverrides` from the auras that carry `overrides`.
 * Called only when such an aura is applied (new instance) or removed. Later auras in `u.auras`
 * (applied later) win a conflict. The field is deleted when empty, so worlds without
 * transforms stay byte-identical.
 */
export function rebuildOverrides(ctx: SimContext, u: Unit): void {
  let map: Record<string, string> | undefined;
  for (const inst of u.auras) {
    const ovs: AbilityOverride[] | undefined = ctx.content.aura(inst.auraId)?.overrides;
    if (!ovs) continue;
    for (const o of ovs) (map ??= {})[o.abilityId] = o.with;
  }
  if (map) u.abilityOverrides = map;
  else delete u.abilityOverrides;
}

/** +basekits: the aura changes max HP (maxHpPct). */
const hasMaxHpMod = (def: AuraDef): boolean => !!def.mods?.some((m) => m.stat === 'maxHpPct');

/** +lead: rotation rework (F12). A grant lost to a stack cap (sim-only event). */
function emitStackOvercap(ctx: SimContext, def: AuraDef, targetId: UnitId, sourceId: UnitId, lost: number): void {
  if (lost > 0) ctx.emit({ type: 'overcap', sourceID: sourceId, targetID: targetId, kind: 'auraStacks', abilityGameID: def.id, amount: lost });
}

function auraEvent(kind: 'buff' | 'debuff', what: 'apply' | 'remove' | 'refresh' | 'applystack' | 'removestack'): AuraEvent['type'] {
  switch (what) {
    case 'apply':
      return kind === 'buff' ? 'applybuff' : 'applydebuff';
    case 'remove':
      return kind === 'buff' ? 'removebuff' : 'removedebuff';
    case 'refresh':
      return kind === 'buff' ? 'refreshbuff' : 'refreshdebuff';
    case 'applystack':
      return kind === 'buff' ? 'applybuffstack' : 'applydebuffstack';
    case 'removestack':
      return kind === 'buff' ? 'removebuffstack' : 'removedebuffstack';
  }
}

/** Whether CC DR applies to this unit/category (PLAN §3: on NPCs only stun; taunt DR lives in threat). */
export function drApplies(u: Unit, cc: CcType): boolean {
  return u.kind === 'player' || cc === 'stun';
}

/**
 * Consume one DR step for a CC category. Returns the duration multiplier (0 = immune).
 * DR resets DR_RESET_MS after the last CC of the category ends.
 */
export function applyDr(ctx: SimContext, u: Unit, cc: CcType, baseDurationMs: Ms): number {
  if (!drApplies(u, cc)) return 1;
  const st = u.dr[cc] ?? { count: 0, resetAt: 0 };
  if (ctx.now >= st.resetAt) st.count = 0;
  const m = DR_MULTIPLIERS[st.count] ?? 0;
  if (m <= 0) return 0;
  st.count += 1;
  st.resetAt = ctx.now + Math.round(baseDurationMs * m) + DR_RESET_MS;
  u.dr[cc] = st;
  return m;
}

function periodicInterval(def: AuraDef, src: Unit | undefined, ctx: SimContext): Ms {
  const p = def.periodic!;
  if (!p.hasteScaled || !src) return p.intervalMs;
  return Math.max(1, Math.round(p.intervalMs / (1 + unitEffectiveStats(ctx, src).haste)));
}

function snapshotOf(ctx: SimContext, src: Unit | undefined): AuraInstance['snapshot'] | undefined {
  if (!src) return undefined;
  const s = unitEffectiveStats(ctx, src);
  return {
    power: s.power,
    crit: s.crit,
    damageDoneMult: s.damageDoneMult * (1 + s.versatility) * src.scaling.damage,
    healingDoneMult: s.healingDoneMult * (1 + s.versatility),
  };
}

/** Apply/refresh/stack an aura from its definition. Returns the instance or null if resisted. */
export function applyAuraDef(ctx: SimContext, def: AuraDef, targetId: UnitId, sourceId: UnitId, abilityId: AbilityId, opts: ApplyAuraOpts = {}): AuraInstance | null {
  const t = unitOf(ctx, targetId);
  if (!t?.alive) return null;
  const src = unitOf(ctx, sourceId);
  const hostile = isHostile(src, t);
  const cs = controlState(ctx, targetId);
  if (def.cc && cs.immuneCc) return null;
  if (def.kind === 'debuff' && hostile && cs.immuneDamage) return null;

  let duration = opts.durationMs ?? def.durationMs;
  if (def.cc && duration > 0) {
    const m = applyDr(ctx, t, def.cc, duration);
    if (m === 0) return null;
    duration = Math.round(duration * m);
  }

  const maxStacks = def.maxStacks ?? (def.stacking === 'stack' ? Number.MAX_SAFE_INTEGER : 1);
  const addStacks = Math.max(1, opts.stacks ?? 1);
  const existing = t.auras.find((a) => a.auraId === def.id && (def.stacking === 'unique' || a.casterId === sourceId));

  if (existing) {
    if (def.stacking === 'ignore') {
      if (def.ui?.proc) emitStackOvercap(ctx, def, targetId, sourceId, addStacks);
      return existing;
    }
    const remaining = existing.expiresAt === null ? 0 : Math.max(0, existing.expiresAt - ctx.now);
    const newDur = duration <= 0 ? 0 : def.pandemic ? Math.min(duration + remaining, Math.round(duration * PANDEMIC_CAP)) : duration;
    existing.expiresAt = newDur > 0 ? ctx.now + newDur : null;
    existing.durationMs = newDur;
    if (def.stacking === 'unique') {
      existing.casterId = sourceId;
      existing.abilityId = abilityId;
    }
    if (def.periodic) {
      // The running tick timer is kept (rolling periodic); only the period is re-hasted.
      existing.periodicIntervalMs = periodicInterval(def, src, ctx);
      const snap = snapshotOf(ctx, src);
      if (snap) existing.snapshot = snap;
    }
    if (opts.absorb !== undefined) existing.absorbRemaining = opts.absorb;
    const prevStacks = existing.stacks;
    // +lead (F12): stacks past the cap (stack auras), or a whole re-grant of a 1-stack proc aura, are lost.
    let lost = 0;
    if (def.stacking === 'stack') {
      existing.stacks = Math.min(maxStacks, existing.stacks + addStacks);
      lost = prevStacks + addStacks - existing.stacks;
    } else if (def.ui?.proc) lost = addStacks;
    if (existing.stacks > prevStacks) {
      ctx.emit({ type: auraEvent(def.kind, 'applystack'), sourceID: sourceId, targetID: targetId, abilityGameID: def.id, stack: existing.stacks });
    }
    emitClean(ctx, { type: auraEvent(def.kind, 'refresh'), sourceID: sourceId, targetID: targetId, abilityGameID: def.id, absorb: existing.absorbRemaining });
    emitStackOvercap(ctx, def, targetId, sourceId, lost);
    if (def.cc) onCcApplied(ctx, t, def.cc);
    return existing;
  }

  const inst: AuraInstance = {
    iid: ctx.newId('a'),
    auraId: def.id,
    casterId: sourceId,
    abilityId,
    appliedAt: ctx.now,
    expiresAt: duration > 0 ? ctx.now + duration : null,
    durationMs: duration,
    stacks: def.stacking === 'stack' ? Math.min(maxStacks, addStacks) : 1,
  };
  if (def.periodic) {
    inst.periodicIntervalMs = periodicInterval(def, src, ctx);
    inst.nextTickAt = ctx.now + inst.periodicIntervalMs;
    const snap = snapshotOf(ctx, src);
    if (snap) inst.snapshot = snap;
  }
  if (def.absorb) inst.absorbRemaining = Math.max(0, Math.round(opts.absorb ?? 0));
  const hpBefore = hasMaxHpMod(def) ? maxHpMult(ctx, t) : 0; // +basekits
  t.auras.push(inst);
  if (hpBefore > 0) rescaleMaxHp(t, maxHpMult(ctx, t) / hpBefore);
  if (def.overrides?.length) rebuildOverrides(ctx, t); // +lead (F2)
  if (def.tally) retally(ctx, t, def, sourceId, opts.depth ?? 0); // +ranger-specs: Thicket counts the HoTs

  emitClean(ctx, {
    type: auraEvent(def.kind, 'apply'),
    sourceID: sourceId,
    targetID: targetId,
    abilityGameID: def.id,
    stack: inst.stacks > 1 ? inst.stacks : undefined,
    absorb: inst.absorbRemaining,
  });
  if (def.stacking === 'stack') emitStackOvercap(ctx, def, targetId, sourceId, addStacks - inst.stacks);

  if (def.cc) onCcApplied(ctx, t, def.cc);
  if (hostile && def.kind === 'debuff' && t.threat) {
    addThreat(ctx, t.id, sourceId, 0);
    ctx.sys.ai.onAttacked(ctx, t.id, sourceId);
  }
  if (def.onApply?.length) runEffects(ctx, def.onApply, { sourceId, abilityId: def.id, targetId, point: null, auraIid: inst.iid, depth: (opts.depth ?? 0) + 1 });
  return inst;
}

export function applyAura(ctx: SimContext, targetId: UnitId, auraId: AuraId, sourceId: UnitId, abilityId: AbilityId, opts: ApplyAuraOpts = {}): AuraInstance | null {
  const def = ctx.content.aura(auraId);
  return def ? applyAuraDef(ctx, def, targetId, sourceId, abilityId, opts) : null;
}

function onCcApplied(ctx: SimContext, t: Unit, cc: CcType): void {
  if (CAST_BREAKING_CC.has(cc) && t.cast) ctx.sys.casting.cancelCast(ctx, t.id, 'cc');
  if (MOVE_BREAKING_CC.has(cc) && t.move && t.move.mode !== 'forced') t.move = null;
}

/** Remove one live instance (events, DR timer, procs, onRemove). */
export function removeInstance(ctx: SimContext, u: Unit, inst: AuraInstance, reason: RemoveReason): void {
  const idx = u.auras.indexOf(inst);
  if (idx < 0) return;
  const def = ctx.content.aura(inst.auraId);
  const hpBefore = def && hasMaxHpMod(def) ? maxHpMult(ctx, u) : 0; // +basekits
  u.auras.splice(idx, 1);
  if (hpBefore > 0) rescaleMaxHp(u, maxHpMult(ctx, u) / hpBefore);
  ctx.emit({ type: auraEvent(def?.kind ?? 'buff', 'remove'), sourceID: inst.casterId, targetID: u.id, abilityGameID: inst.auraId, reason });
  if (!def) return;
  if (def.overrides?.length) rebuildOverrides(ctx, u); // +lead (F2)
  if (def.tally) retally(ctx, u, def, inst.casterId); // +ranger-specs: Thicket counts the HoTs
  if (def.cc && drApplies(u, def.cc)) {
    const st = u.dr[def.cc];
    if (st) st.resetAt = ctx.now + DR_RESET_MS;
  }
  const depth = 1;
  def.procs?.forEach((p, i) => {
    if (p.on === 'auraRemoved' || (p.on === 'auraExpire' && reason === 'expired')) runProc(ctx, u, inst, p, i, u.id, depth);
  });
  if (def.onRemove?.length) runEffects(ctx, def.onRemove, { sourceId: inst.casterId, abilityId: def.id, targetId: u.id, point: null, auraIid: inst.iid, depth });
  if (def.damageShare) payShare(ctx, u, inst, def); // +acolyte-specs (S10): Martyr heals back what it took
}

export function removeAura(
  ctx: SimContext,
  targetId: UnitId,
  which: { iid?: AuraInstanceId; auraId?: AuraId; casterId?: UnitId },
  reason: RemoveReason,
): void {
  const u = unitOf(ctx, targetId);
  if (!u) return;
  const matches = u.auras.filter((a) =>
    which.iid !== undefined ? a.iid === which.iid : a.auraId === which.auraId && (which.casterId === undefined || a.casterId === which.casterId),
  );
  for (const inst of matches) removeInstance(ctx, u, inst, reason);
}

/** Remove `n` stacks; drops the aura when it reaches 0. */
export function removeStacks(ctx: SimContext, u: Unit, inst: AuraInstance, n: number, reason: RemoveReason): void {
  if (!u.auras.includes(inst)) return;
  inst.stacks -= n;
  if (inst.stacks <= 0) {
    removeInstance(ctx, u, inst, reason);
    return;
  }
  const def = ctx.content.aura(inst.auraId);
  ctx.emit({ type: auraEvent(def?.kind ?? 'buff', 'removestack'), sourceID: inst.casterId, targetID: u.id, abilityGameID: inst.auraId, stack: inst.stacks, reason });
}

/**
 * Effective proc chance including bad-luck protection (F4): min(max ?? 1, chance + step × misses).
 * Pure; exported for tooltips / harness.
 */
export function procChance(p: ProcSpec, misses: number): number {
  const base = p.chance ?? 1;
  if (!p.badLuck) return base;
  return Math.min(p.badLuck.max ?? 1, base + p.badLuck.step * misses);
}

function runProc(ctx: SimContext, holder: Unit, inst: AuraInstance, p: ProcSpec, idx: number, otherId: UnitId | null, depth: number): boolean {
  if (depth > MAX_EFFECT_DEPTH) return false;
  // +lead (F5): the gate runs before the ICD and the roll; a failed gate is not a miss.
  if (p.if && !evalCondition(ctx, p.if, holder.id, otherId ?? holder.id)) return false;
  const last = inst.procIcd?.[idx];
  if (p.icdMs && last !== undefined && ctx.now - last < p.icdMs) return false;
  // +lead (F4): one ctx.rng roll; chance >= 1 never rolls (keeps RNG streams of certain procs unchanged).
  const misses = inst.procMiss?.[idx] ?? 0;
  const chance = procChance(p, misses);
  if (chance < 1 && !ctx.rng.chance(chance)) {
    if (p.badLuck) (inst.procMiss ??= {})[idx] = misses + 1;
    return false;
  }
  if (misses > 0 && inst.procMiss) {
    delete inst.procMiss[idx];
    if (Object.keys(inst.procMiss).length === 0) delete inst.procMiss;
  }
  if (p.icdMs) inst.procIcd = { ...inst.procIcd, [idx]: ctx.now };
  runEffects(ctx, p.effects, { sourceId: holder.id, abilityId: inst.auraId, targetId: otherId ?? holder.id, point: null, auraIid: inst.iid, depth: depth + 1 });
  if (p.consumeStack) removeStacks(ctx, holder, inst, 1, 'consumed');
  return true;
}

/** Extra event data some triggers filter on. */
export interface ProcEventInfo {
  /** 'avoided': the defender's hit type (filtered by ProcSpec.hitTypes). */
  hitType?: HitType;
  /** 'castResolved': start of the resolving cast (same-cast guard). Default now. */
  castStartedAt?: Ms;
  /** 'castResolved': world.nextSeq at the cast start (orders same-millisecond applications). */
  castStartSeq?: number;
}

/** Creation sequence of an aura instance (iids come from ctx.newId('a') = 'a<world.nextSeq>'); NaN if unknown. */
function instSeq(inst: AuraInstance): number {
  return Number(inst.iid.slice(1));
}

/**
 * +lead: rotation rework (F3). Same-cast guard: was this aura instance created at or after the start
 * of the resolving cast? Uses the creation sequence when both are known (exact even within one tick),
 * else the timestamps.
 */
export function appliedDuringCast(inst: AuraInstance, castStartedAt: Ms, castStartSeq: number | undefined): boolean {
  if (inst.appliedAt > castStartedAt) return true;
  if (inst.appliedAt < castStartedAt) return false;
  const seq = instSeq(inst);
  if (castStartSeq === undefined || !Number.isFinite(seq)) return true;
  return seq >= castStartSeq;
}

/** Fire a holder's aura procs for an event. `otherId` is the event's other party. */
export function fireProcs(ctx: SimContext, holderId: UnitId, on: ProcSpec['on'], otherId: UnitId | null, triggerAbilityId: AbilityId, depth: number, info: ProcEventInfo = {}): void {
  const h = unitOf(ctx, holderId);
  if (!h?.alive || depth > MAX_EFFECT_DEPTH) return;
  // +lead (F3): castResolved runs consumeStack procs first, then the rest; never on an aura
  // instance applied at or after the resolving cast's start (the same-cast guard).
  const resolved = on === 'castResolved';
  const castStart = info.castStartedAt ?? ctx.now;
  const passes: readonly (boolean | null)[] = resolved ? [true, false] : [null];
  for (const consumePass of passes) {
    for (const inst of [...h.auras]) {
      const procs = ctx.content.aura(inst.auraId)?.procs;
      if (!procs) continue;
      if (resolved && appliedDuringCast(inst, castStart, info.castStartSeq)) continue;
      procs.forEach((p, i) => {
        if (p.on !== on || !h.auras.includes(inst)) return;
        if (consumePass !== null && !!p.consumeStack !== consumePass) return;
        if (p.abilityIds && !p.abilityIds.includes(triggerAbilityId)) return;
        if (on === 'avoided' && !(p.hitTypes ?? DEFAULT_AVOID_TYPES).includes(info.hitType ?? 'dodge')) return;
        // An aura's own periodic/proc effects never re-trigger it.
        if (triggerAbilityId === inst.auraId) return;
        runProc(ctx, h, inst, p, i, otherId, depth);
      });
    }
  }
}

function periodicTick(ctx: SimContext, u: Unit, inst: AuraInstance, def: AuraDef, scale: number): void {
  const ectx = { sourceId: inst.casterId, abilityId: inst.auraId, targetId: u.id, point: null, periodic: true, auraIid: inst.iid, depth: 1, ...(inst.snapshot ? { snapshot: inst.snapshot } : {}) };
  runEffects(ctx, def.periodic!.effects, ectx, scale * inst.stacks);
}

/** Periodic ticks (timer accumulation), expirations (with optional partial final tick), DR cleanup. */
export function tickAuras(ctx: SimContext): void {
  tickEchoes(ctx); // +rogue-specs: Silhouette echoes due this tick (combat/clones.ts)
  tickPets(ctx); // +acolyte-specs (S7): friendly pets follow, attack, expire (combat/pets.ts)
  tickCompanions(ctx); // +ranger-specs (S7 companions): the Beastbound's spirit beast (combat/pets.ts)
  for (const id of ctx.world.unitOrder) {
    const u = ctx.world.units[id];
    if (!u?.alive) continue;
    for (const inst of [...u.auras]) {
      if (!u.auras.includes(inst)) continue;
      const def = ctx.content.aura(inst.auraId);
      if (!def) continue;
      const interval = inst.periodicIntervalMs;
      if (def.periodic && interval && inst.nextTickAt !== undefined) {
        const end = inst.expiresAt ?? Number.MAX_SAFE_INTEGER;
        while (inst.nextTickAt <= ctx.now && inst.nextTickAt <= end && u.alive && u.auras.includes(inst)) {
          periodicTick(ctx, u, inst, def, 1);
          inst.nextTickAt += inst.periodicIntervalMs ?? interval;
        }
      }
      if (inst.expiresAt !== null && inst.expiresAt <= ctx.now && u.alive && u.auras.includes(inst)) {
        if (def.periodic?.partialFinalTick && interval && inst.nextTickAt !== undefined) {
          const frac = (inst.expiresAt - (inst.nextTickAt - interval)) / interval;
          if (frac > 0 && frac < 1) periodicTick(ctx, u, inst, def, frac);
        }
        removeInstance(ctx, u, inst, 'expired');
      }
    }
    for (const k of Object.keys(u.dr) as CcType[]) {
      const st = u.dr[k];
      if (st && st.count > 0 && ctx.now >= st.resetAt) delete u.dr[k];
    }
  }
}
